import { ArrowDown, ArrowUp } from 'lucide-react';
import { AppModal } from './AppModal';
import { ModalTopBar } from './ModalTopBar';
import { SectionLabel } from './typography';

import type { SortDirection, SortFieldOption } from '@/shared/lib/sort';
import { ChipSelect, type ChipOption } from './ChipSelect';
import styles from './SortSheet.module.css';

interface SortSheetProps<TField extends string> {
  isOpen: boolean;
  onClose: () => void;
  fields: SortFieldOption<TField>[];
  sortBy: TField;
  sortDirection: SortDirection;
  onSortByChange: (value: TField) => void;
  onSortDirectionChange: (value: SortDirection) => void;
  ariaLabel?: string;
  testId?: string;
}

const directionOptions: ChipOption<SortDirection>[] = [
  {
    label: 'Ascending',
    value: 'asc',
    before: <ArrowUp size={16} aria-hidden="true" />,
  },
  {
    label: 'Descending',
    value: 'desc',
    before: <ArrowDown size={16} aria-hidden="true" />,
  },
];

export const SortSheet = <TField extends string>({
  isOpen,
  onClose,
  fields,
  sortBy,
  sortDirection,
  onSortByChange,
  onSortDirectionChange,
  ariaLabel = 'Sort',
  testId,
}: SortSheetProps<TField>) => (
  <AppModal
    open={isOpen}
    onOpenChange={(open) => {
      if (!open) onClose();
    }}
    className={styles.modal}
    aria-label={ariaLabel}
    data-testid={testId}
  >
    <ModalTopBar title="Sort" onCancel={onClose} />

    <div className={styles.group}>
      <SectionLabel className={styles.groupLabel}>Sort by</SectionLabel>
      <ChipSelect
        options={fields}
        value={sortBy}
        onChange={onSortByChange}
        ariaLabel="Sort field"
        chipTestId={(value) => `sort-field-${value}`}
        flush
        fill
      />
    </div>

    <div className={styles.group}>
      <SectionLabel className={styles.groupLabel}>Order</SectionLabel>
      <ChipSelect
        options={directionOptions}
        value={sortDirection}
        onChange={onSortDirectionChange}
        ariaLabel="Sort order"
        chipTestId={(value) => `sort-direction-${value}`}
        flush
        fill
      />
    </div>
  </AppModal>
);
